import { Link } from 'react-router-dom'
import { XendrelLogo } from '@/components/brand/XendrelLogo'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import { Reveal } from './primitives'
import {
  AgentsSection,
  ClosingSection,
  ExtractionSection,
  FormatsSection,
  LanguagesSection,
  OnPremiseSection,
  OsintSection,
  PipelinesSection,
  ProvenanceSection,
  StatStrip,
  TeamsSection,
  ViewsSection,
} from './sections'
import { MarkField } from './visuals'

const NAV_LINKS = [
  { href: '#extraction', label: 'Extraction' },
  { href: '#research', label: 'Agents' },
  { href: '#osint', label: 'OSINT' },
  { href: '#on-premise', label: 'On-premise' },
  { href: '#formats', label: 'Formats' },
  { href: '#views', label: 'Views' },
]

const NAV_LINK_CLASS =
  'font-mono text-2xs uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground'

const LOGO_CLASS = 'h-6 text-foreground'

function TopNav({ scrolled }: { scrolled: boolean }) {
  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
        scrolled ? 'border-b border-border/60 bg-background/85 backdrop-blur-md' : 'bg-transparent'
      )}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <a href="#top" aria-label="Xendrel — back to top">
          <XendrelLogo className={LOGO_CLASS} />
        </a>
        <nav className="hidden items-center gap-7 md:flex" aria-label="Page sections">
          {NAV_LINKS.map(link => (
            <a key={link.href} href={link.href} className={NAV_LINK_CLASS}>
              {link.label}
            </a>
          ))}
        </nav>
        <Button asChild size="sm" className="h-9 md:h-8">
          <Link to="/login">Open Xendrel</Link>
        </Button>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <div className="relative overflow-hidden">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 pt-36 pb-20 md:pt-44 md:pb-24 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-mono text-2xs uppercase tracking-[0.35em] text-primary-strong">
              On-premise decision intelligence
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-brand text-[2.75rem] font-semibold leading-[1.04] tracking-tight text-foreground sm:text-5xl md:text-7xl">
              From scattered sources
              <br />
              <span className="text-muted-foreground">to sound decisions.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Xendrel connects documents, spreadsheets, scans, recordings, and open sources into one
              picture you can act on — in 176+ languages, entirely on your infrastructure, with
              every fact traceable to its source.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-10 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Button asChild size="lg" className="px-8">
                <Link to="/login">Open Xendrel</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="px-6 text-muted-foreground">
                <a href="#extraction">
                  Explore the platform <span aria-hidden="true">↓</span>
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="hidden lg:col-span-5 lg:block">
          <MarkField className="w-full text-foreground/25" />
        </Reveal>
      </div>
      <div className="mx-auto max-w-6xl px-6 pb-4">
        <StatStrip />
      </div>
    </div>
  )
}

function Footer() {
  return (
    <footer className="border-t border-border/60">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-8 px-6 py-12 md:flex-row">
        <XendrelLogo className={LOGO_CLASS} />
        <nav
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 md:gap-6"
          aria-label="Footer navigation"
        >
          {NAV_LINKS.map(link => (
            <a key={link.href} href={link.href} className={cn(NAV_LINK_CLASS, 'py-2 md:py-0')}>
              {link.label}
            </a>
          ))}
          <Link to="/login" className={cn(NAV_LINK_CLASS, 'py-2 text-primary-strong md:py-0')}>
            Open platform
          </Link>
        </nav>
        <p className="font-mono text-2xs tracking-[0.2em] text-muted-foreground">© 2026 XENDREL</p>
      </div>
    </footer>
  )
}

export interface InfoLandingViewProps {
  /** True once the page is scrolled; drives the sticky-nav backdrop. */
  scrolled: boolean
}

/**
 * Pure presentation for the public `/info` landing page (Xendrel rebrand).
 * Token-only styling — the page adapts to light and dark themes and stays
 * within the frontend color rules. Scroll state is owned by `pages/info`.
 */
export function InfoLandingView({ scrolled }: InfoLandingViewProps) {
  return (
    <div id="top" className="min-h-screen bg-background text-foreground">
      <TopNav scrolled={scrolled} />
      <main>
        <Hero />
        <ExtractionSection />
        <PipelinesSection />
        <OnPremiseSection />
        <LanguagesSection />
        <AgentsSection />
        <OsintSection />
        <ProvenanceSection />
        <FormatsSection />
        <ViewsSection />
        <TeamsSection />
        <ClosingSection />
      </main>
      <Footer />
    </div>
  )
}
